// Archivo: src/pages/AdminServiceSections.js

import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "../components/RichTextContent.css";
import { getPublicServiceById } from "../services/service.service";
import {
  getServiceSections,
  deleteSection,
  createSection,
  restoreSection,
  updateSection,
  deleteFiles,
} from "../services/admin.service";

// Iconos
const BackIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
  </svg>
);

const AddIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
    />
  </svg>
);

const RestoreIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const EditIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const VideoIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </svg>
);

const DocumentIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const DeleteIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

// Componente de confirmación de eliminación con verificación adicional
const DeleteConfirmModal = ({ section, onConfirm, onCancel, isDeleting }) => {
  const [confirmationText, setConfirmationText] = useState("");
  const [showExtraConfirmation, setShowExtraConfirmation] = useState(false);

  const isPermanentDelete = section.deleted_at !== null;
  const sectionTitle = section.title;
  const isConfirmationValid =
    confirmationText.trim().toLowerCase() === sectionTitle.toLowerCase();

  const handleFirstConfirm = () => {
    if (isPermanentDelete) {
      setShowExtraConfirmation(true);
    } else {
      onConfirm();
    }
  };

  const handleFinalConfirm = () => {
    if (isConfirmationValid) {
      onConfirm();
    }
  };

  const resetModal = () => {
    setConfirmationText("");
    setShowExtraConfirmation(false);
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {isPermanentDelete
            ? "Eliminar permanentemente"
            : "Desactivar sección"}
        </h3>

        {!showExtraConfirmation ? (
          <>
            <div
              className={`p-4 rounded-lg mb-4 ${
                isPermanentDelete
                  ? "bg-red-50 border border-red-200"
                  : "bg-yellow-50 border border-yellow-200"
              }`}
            >
              <p
                className={`font-medium mb-2 ${
                  isPermanentDelete ? "text-red-800" : "text-yellow-800"
                }`}
              >
                {isPermanentDelete
                  ? "⚠️ ACCIÓN IRREVERSIBLE"
                  : "⚠️ ACCIÓN DE DESACTIVACIÓN"}
              </p>
              <p
                className={
                  isPermanentDelete ? "text-red-700" : "text-yellow-700"
                }
              >
                {isPermanentDelete
                  ? "Esta acción eliminará permanentemente todos los datos, videos y documentos asociados a esta sección."
                  : "Esta acción desactivará la sección, pero podrás restaurarla más tarde."}
              </p>
            </div>

            <p className="text-gray-600 mb-6">
              {isPermanentDelete
                ? `¿Estás seguro de que quieres eliminar permanentemente la sección "${sectionTitle}"?`
                : `¿Estás seguro de que quieres desactivar la sección "${sectionTitle}"?`}
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={resetModal}
                disabled={isDeleting}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleFirstConfirm}
                disabled={isDeleting}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                  isPermanentDelete
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-yellow-600 hover:bg-yellow-700"
                }`}
              >
                {isPermanentDelete ? "Continuar" : "Desactivar"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
              <p className="font-medium text-red-800 mb-2">
                🚨 VERIFICACIÓN FINAL REQUERIDA
              </p>
              <p className="text-red-700 text-sm">
                Para confirmar la eliminación permanente, escribe exactamente el
                nombre de la sección:
              </p>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Escribe:{" "}
                <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                  {sectionTitle}
                </span>
              </p>
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="Nombre exacto de la sección"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                disabled={isDeleting}
                autoFocus
              />
              {confirmationText && !isConfirmationValid && (
                <p className="text-red-600 text-sm mt-1">
                  El texto no coincide exactamente
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowExtraConfirmation(false)}
                disabled={isDeleting}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Volver
              </button>
              <button
                onClick={resetModal}
                disabled={isDeleting}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleFinalConfirm}
                disabled={isDeleting || !isConfirmationValid}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? "Eliminando..." : "Eliminar permanentemente"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Componente para gestionar URLs de YouTube existentes
const ExistingVideosList = ({ videos, onRemove, isRemoving }) => {
  if (!videos || videos.length === 0) return null;

  return (
    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <h4 className="text-sm font-semibold text-blue-800 mb-3">
        Videos existentes ({videos.length}):
      </h4>
      <div className="space-y-2 max-h-32 overflow-y-auto">
        {videos.map((video, index) => (
          <div
            key={video.id || index}
            className="flex items-center justify-between bg-white p-2 rounded border"
          >
            <div className="flex-1 min-w-0">
              <p className="text-xs text-blue-600 truncate">{video.url}</p>
            </div>
            <button
              type="button"
              onClick={() => onRemove(video, index)}
              disabled={isRemoving}
              className="ml-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
              title="Eliminar video"
            >
              <DeleteIcon />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const STORAGE_URL = process.env.REACT_APP_STORAGE_URL;

// Componente para gestionar documentos existentes
const ExistingDocumentsList = ({ documents, onRemove, isRemoving }) => {
  if (!documents || documents.length === 0) return null;

  return (
    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
      <h4 className="text-sm font-semibold text-green-800 mb-3">
        Documentos existentes ({documents.length}):
      </h4>
      <div className="space-y-2 max-h-32 overflow-y-auto">
        {documents.map((doc, index) => (
          <div
            key={doc.id || index}
            className="flex items-center justify-between bg-white p-2 rounded border"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-800 truncate">
                {doc.title || doc.name || `Documento ${index + 1}`}
              </p>
              {doc.path && (
                <a
                  href={`${STORAGE_URL}/${doc.path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-green-600 hover:text-green-800 truncate block"
                >
                  {doc.path}
                </a>
              )}
            </div>
            <button
              type="button"
              onClick={() => onRemove(doc, index)}
              disabled={isRemoving}
              className="ml-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
              title="Eliminar documento"
            >
              <DeleteIcon />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Componente de formulario para nueva/editar sección (ACTUALIZADO)
const SectionForm = ({
  serviceId,
  section = null,
  onSubmit,
  onCancel,
  isSubmitting,
  title,
  onRemoveExistingVideo,
  onRemoveExistingDocument,
}) => {
  // Extraer URLs de YouTube correctamente
  const getInitialUrls = () => {
    if (!section) return [""];

    if (section.youtube_links && Array.isArray(section.youtube_links)) {
      const urls = section.youtube_links.map((link) => link.url || link);
      return urls.length > 0 ? urls : [""];
    }

    if (section.urls && Array.isArray(section.urls)) {
      return section.urls.length > 0 ? section.urls : [""];
    }

    return [""];
  };

  // Inicializar EditorState
  const getInitialEditorState = () => {
    if (section?.description) {
      const contentBlock = htmlToDraft(section.description);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks,
        );
        return EditorState.createWithContent(contentState);
      }
    }
    return EditorState.createEmpty();
  };

  const [formData, setFormData] = useState({
    title: section?.title || "",
    description: section?.description || "",
    urls: getInitialUrls(),
    documents: [], // Archivos para upload
  });

  const [editorState, setEditorState] = useState(getInitialEditorState());

  const [selectedFiles, setSelectedFiles] = useState([]); // Archivos seleccionados para mostrar
  const [isRemovingContent, setIsRemovingContent] = useState(false);

  // Estado para mostrar archivos existentes
  const [existingFiles, setExistingFiles] = useState({
    videos: [],
    documents: [],
  });

  // Efecto para cargar datos existentes cuando cambia la sección
  useEffect(() => {
    if (section) {
      const videos = section.youtube_links || [];
      const documents = section.documents || [];

      setExistingFiles({
        videos: videos,
        documents: documents,
      });
    }
  }, [section]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    // Convertir el contenido del editor a HTML
    const rawContentState = convertToRaw(editorState.getCurrentContent());
    const htmlDescription = draftToHtml(rawContentState);

    const dataToSubmit = {
      title: formData.title.trim(),
      description: htmlDescription,
      service_id: parseInt(serviceId),
      user_id: 1, // TODO: Obtener del contexto de usuario
      urls: formData.urls.filter((url) => url.trim()),
      documents: formData.documents,
    };

    onSubmit(dataToSubmit);
  };

  // Funciones para manejar URLs de YouTube
  const addUrlField = () => {
    setFormData((prev) => ({
      ...prev,
      urls: [...prev.urls, ""],
    }));
  };

  const removeUrlField = (index) => {
    setFormData((prev) => ({
      ...prev,
      urls: prev.urls.filter((_, i) => i !== index),
    }));
  };

  const updateUrl = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      urls: prev.urls.map((url, i) => (i === index ? value : url)),
    }));
  };

  // Funciones para manejar archivos
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);

    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setFormData((prev) => ({
      ...prev,
      documents: [...prev.documents, ...newFiles],
    }));

    e.target.value = "";
  };

  const removeSelectedFile = (indexToRemove) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
    setFormData((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, index) => index !== indexToRemove),
    }));
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    setFormData((prev) => ({
      ...prev,
      documents: [],
    }));
  };

  // Manejar eliminación de videos existentes
  const handleRemoveExistingVideo = async (video, index) => {
    if (!onRemoveExistingVideo) return;

    setIsRemovingContent(true);
    try {
      await onRemoveExistingVideo(video, index);
      // Actualizar estado local
      setExistingFiles((prev) => ({
        ...prev,
        videos: prev.videos.filter((_, i) => i !== index),
      }));
    } catch (error) {
      console.error("Error removing video:", error);
    } finally {
      setIsRemovingContent(false);
    }
  };

  // Manejar eliminación de documentos existentes
  const handleRemoveExistingDocument = async (document, index) => {
    if (!onRemoveExistingDocument) return;

    setIsRemovingContent(true);
    try {
      await onRemoveExistingDocument(document, index);
      // Actualizar estado local
      setExistingFiles((prev) => ({
        ...prev,
        documents: prev.documents.filter((_, i) => i !== index),
      }));
    } catch (error) {
      console.error("Error removing document:", error);
    } finally {
      setIsRemovingContent(false);
    }
  };

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 p-6"
      data-edit-form
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

      {/* Mostrar archivos existentes cuando se está editando */}
      {section && (
        <>
          <ExistingVideosList
            videos={existingFiles.videos}
            onRemove={handleRemoveExistingVideo}
            isRemoving={isRemovingContent}
          />
          <ExistingDocumentsList
            documents={existingFiles.documents}
            onRemove={handleRemoveExistingDocument}
            isRemoving={isRemovingContent}
          />
        </>
      )}

      {/* Mostrar mensaje si no hay contenido existente al editar */}
      {section &&
        existingFiles.videos.length === 0 &&
        existingFiles.documents.length === 0 && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-700">
              Esta sección no tiene contenido multimedia. Usa los campos de
              abajo para agregar videos y documentos.
            </p>
          </div>
        )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Título *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, title: e.target.value }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gob-azul-medio focus:border-transparent"
            placeholder="Título de la sección"
            required
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descripción
          </label>
          <div className="border border-gray-300 rounded-lg min-h-[200px]">
            <Editor
              editorState={editorState}
              onEditorStateChange={setEditorState}
              wrapperClassName="wrapper-class"
              editorClassName="editor-class px-3"
              toolbarClassName="toolbar-class"
              placeholder="Descripción de la sección"
              readOnly={isSubmitting}
              toolbar={{
                options: [
                  "inline",
                  "blockType",
                  "list",
                  "textAlign",
                  // "colorPicker",
                  // "link",
                  "remove",
                  "history",
                ],
                inline: {
                  options: ["bold", "italic", "underline", "strikethrough"],
                },
                blockType: {
                  inDropdown: false,
                  options: ["Normal", "H1", "H2", "H3"],
                },
                list: {
                  options: ["unordered", "ordered"],
                },
              }}
            />
          </div>
        </div>

        {/* URLs de YouTube */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {section ? "Agregar nuevas URLs de YouTube" : "URLs de YouTube"}
          </label>
          {formData.urls.map((url, index) => (
            <div key={index} className="flex gap-2 mb-2">
              <input
                type="url"
                value={url}
                onChange={(e) => updateUrl(index, e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gob-azul-medio focus:border-transparent"
                placeholder="https://youtube.com/watch?v=..."
                disabled={isSubmitting}
              />
              {formData.urls.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeUrlField(index)}
                  disabled={isSubmitting}
                  className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addUrlField}
            disabled={isSubmitting}
            className="text-sm text-gob-azul-medio hover:text-gob-azul-oscuro"
          >
            + Agregar otra URL de video
          </button>
        </div>

        {/* Upload de archivos */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {section ? "Subir nuevos archivos" : "Subir archivos"}
          </label>

          <input
            type="file"
            multiple
            onChange={handleFileChange}
            disabled={isSubmitting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gob-azul-medio focus:border-transparent"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          />

          <p className="text-xs text-gray-500 mt-1">
            Puedes seleccionar múltiples archivos. Formatos: PDF, DOC, DOCX,
            XLS, XLSX, PPT, PPTX, TXT
          </p>

          {selectedFiles.length > 0 && (
            <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-medium text-gray-700">
                  Archivos seleccionados ({selectedFiles.length})
                </h5>
                <button
                  type="button"
                  onClick={clearAllFiles}
                  disabled={isSubmitting}
                  className="text-xs text-red-600 hover:text-red-800 underline"
                >
                  Limpiar todo
                </button>
              </div>

              <ul className="space-y-2 max-h-32 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <li
                    key={index}
                    className="flex items-center justify-between bg-white p-2 rounded border"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSelectedFile(index)}
                      disabled={isSubmitting}
                      className="ml-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                      title="Eliminar archivo"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        ></path>
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !formData.title.trim()}
            className="px-4 py-2 bg-gob-azul-medio text-white rounded-lg hover:bg-gob-azul-oscuro disabled:opacity-50"
          >
            {isSubmitting
              ? "Guardando..."
              : section
                ? "Actualizar Sección"
                : "Crear Sección"}
          </button>
        </div>
      </form>
    </div>
  );
};

// Componente principal
export default function AdminServiceSections() {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  // Función recargar secciones
  const reloadSections = async () => {
    try {
      const sectionsData = await getServiceSections(id);
      setSections(sectionsData.data || []);
    } catch (err) {
      console.error("Error al recargar secciones:", err);
      setError("Error al recargar las secciones: " + err.message);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id || isNaN(parseInt(id))) {
        setError("ID de servicio inválido");
        setLoading(false);
        return;
      }

      try {
        const [serviceData, sectionsData] = await Promise.all([
          getPublicServiceById(id),
          getServiceSections(id),
        ]);

        setService(serviceData);
        setSections(sectionsData.data || []);
      } catch (err) {
        setError("Error al cargar los datos: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleCreateSection = async (sectionData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await createSection(sectionData);
      setSuccessMessage("Sección creada exitosamente");
      setShowNewForm(false);
      await reloadSections();
    } catch (err) {
      console.error("Error al crear sección:", err);
      setError("Error al crear la sección: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSection = async (section) => {
    try {
      // Obtener los datos completos de la sección (con videos y documentos)
      const fullSectionData = await getServiceSections(id);
      const completeSectionData = fullSectionData.data.find(
        (s) => s.id === section.id,
      );

      setEditingSection(completeSectionData || section);
      setShowNewForm(false);

      // Scroll hacia el formulario de edición
      setTimeout(() => {
        const editArea = document.getElementById("edit-section-area");
        if (editArea) {
          editArea.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 500);
    } catch (error) {
      console.error("Error loading complete section data:", error);
      setEditingSection(section);
      setShowNewForm(false);
    }
  };

  const handleUpdateSection = async (sectionData) => {
    setIsSubmitting(true);
    try {
      await updateSection(editingSection.id, sectionData);
      setSuccessMessage("Sección actualizada exitosamente");
      setEditingSection(null);
      await reloadSections();
    } catch (err) {
      setError("Error al actualizar la sección: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSection = async (section) => {
    setIsDeleting(true);
    try {
      const force = section.deleted_at !== null;
      await deleteSection(section.id, force);

      setSuccessMessage(
        force
          ? "Sección eliminada permanentemente"
          : "Sección desactivada exitosamente",
      );
      setDeleteModal(null);
      await reloadSections();
    } catch (err) {
      setError("Error al eliminar la sección: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestoreSection = async (sectionId) => {
    setIsRestoring(true);
    try {
      await restoreSection(sectionId);
      setSuccessMessage("Sección restaurada exitosamente");
      await reloadSections();
    } catch (err) {
      setError("Error al restaurar la sección: " + err.message);
    } finally {
      setIsRestoring(false);
    }
  };

  // Función para eliminar video existente
  const handleRemoveExistingVideo = async (video, index) => {
    try {
      setSuccessMessage(
        "Video eliminado de la vista local. Guarda los cambios para confirmar.",
      );
      return Promise.resolve();
    } catch (error) {
      setError("Error al eliminar el video: " + error.message);
      throw error;
    }
  };

  // Función para eliminar documento existente
  const handleRemoveExistingDocument = async (document, index) => {
    try {
      if (document.path) {
        await deleteFiles([document.path]);
        setSuccessMessage("Documento eliminado exitosamente");

        await reloadSections();

        if (editingSection && editingSection.id === document.documentable_id) {
          const fullSectionData = await getServiceSections(id);
          const updatedSection = fullSectionData.data.find(
            (s) => s.id === editingSection.id,
          );
          setEditingSection(updatedSection || editingSection);
        }
      }
    } catch (error) {
      setError("Error al eliminar el documento: " + error.message);
      throw error;
    }
  };

  // Limpiar mensajes después de 5 segundos
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gob-azul-medio mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando secciones...</p>
        </div>
      </div>
    );
  }

  if (error && !service) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-800 mb-2">Error</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <Link
          to="/admin"
          className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          <BackIcon />
          <span className="ml-2">Volver al panel</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Gestionar Secciones
          </h1>
          <p className="text-gray-600 mt-1">
            Servicio: {service?.name} - {sections.length} secciones encontradas
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              setShowNewForm(!showNewForm);
              setEditingSection(null);
            }}
            className="inline-flex items-center px-4 py-2 bg-gob-azul-medio text-white rounded-lg hover:bg-gob-azul-oscuro transition-colors"
          >
            <AddIcon />
            <span className="ml-2">Nueva Sección</span>
          </button>
          <Link
            to="/admin"
            className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <BackIcon />
            <span className="ml-2">Volver</span>
          </Link>
        </div>
      </div>

      {/* Mensajes */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Formulario de nueva sección */}
      {showNewForm && (
        <SectionForm
          serviceId={id}
          onSubmit={handleCreateSection}
          onCancel={() => setShowNewForm(false)}
          isSubmitting={isSubmitting}
          title="Nueva Sección"
        />
      )}

      {/* Formulario de edición */}
      {editingSection && (
        <div id="edit-section-area">
          <SectionForm
            serviceId={id}
            section={editingSection}
            onSubmit={handleUpdateSection}
            onCancel={() => setEditingSection(null)}
            isSubmitting={isSubmitting}
            title={`Editar: ${editingSection.title}`}
            onRemoveExistingVideo={handleRemoveExistingVideo}
            onRemoveExistingDocument={handleRemoveExistingDocument}
          />
        </div>
      )}

      {/* Lista de secciones */}
      <div className="space-y-4">
        {sections.length > 0 ? (
          sections.map((section) => (
            <div
              key={section.id}
              className={`bg-white rounded-lg border border-gray-200 p-6 ${
                section.deleted_at ? "opacity-60 bg-gray-50" : ""
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {section.title}
                    </h3>
                    {section.deleted_at && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                        Desactivada
                      </span>
                    )}
                  </div>

                  <div
                    className="text-gray-600 mb-10 rich-text-content"
                    dangerouslySetInnerHTML={{
                      __html: section.description || "<p>Sin descripción</p>",
                    }}
                  />

                  {/* Estadísticas de contenido */}
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <VideoIcon />
                      <span className="ml-1">
                        {section.youtube_links?.length || 0} videos
                      </span>
                    </div>
                    <div className="flex items-center">
                      <DocumentIcon />
                      <span className="ml-1">
                        {section.documents?.length || 0} documentos
                      </span>
                    </div>
                    <span>ID: {section.id}</span>
                    {section.deleted_at && (
                      <span>
                        Eliminada:{" "}
                        {new Date(section.deleted_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Acciones */}
                <div className="flex space-x-2">
                  {!section.deleted_at ? (
                    <>
                      <a
                        href={`/servicios/${id}/secciones/${section.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm inline-block"
                      >
                        Ver
                      </a>
                      <button
                        onClick={() => handleEditSection(section)}
                        className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
                      >
                        <EditIcon />
                      </button>
                      <Link
                        to={`/admin/servicios/${id}/secciones/${section.id}/subsecciones`}
                        className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm inline-block"
                      >
                        Subsecciones
                      </Link>
                      <button
                        onClick={() => setDeleteModal(section)}
                        className="px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm"
                      >
                        Desactivar
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleRestoreSection(section.id)}
                        disabled={isRestoring}
                        className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50 flex items-center"
                      >
                        <RestoreIcon />
                        <span className="ml-1">
                          {isRestoring ? "Restaurando..." : "Restaurar"}
                        </span>
                      </button>
                      <button
                        onClick={() => setDeleteModal(section)}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                      >
                        Eliminar
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay secciones
            </h3>
            <p className="text-gray-500 mb-4">
              Crea la primera sección para este servicio.
            </p>
            <button
              onClick={() => setShowNewForm(true)}
              className="inline-flex items-center px-4 py-2 bg-gob-azul-medio text-white rounded-lg hover:bg-gob-azul-oscuro"
            >
              <AddIcon />
              <span className="ml-2">Crear Primera Sección</span>
            </button>
          </div>
        )}
      </div>

      {/* Modal de confirmación de eliminación */}
      {deleteModal && (
        <DeleteConfirmModal
          section={deleteModal}
          onConfirm={() => handleDeleteSection(deleteModal)}
          onCancel={() => setDeleteModal(null)}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
}
